/**
 * AssetAiAssistant — dropped into the "Register Asset" ComposeModal as a
 * top-slot panel. Owner pastes machine specs (Hitachi datasheet, dealer
 * email, whatever) → GPT-5.4 extracts Asset Type / Category / Model /
 * Engine Config / Serial Number → the form auto-fills.
 *
 * Talks to the LOCAL portal backend (`/api/ai/asset-extract`) which owns
 * the Emergent LLM key.
 */
import { useState } from "react";
import axios from "axios";
import { Sparkles, Loader2, Check, AlertTriangle } from "lucide-react";
import { apiError } from "@/lib/api";

const PORTAL_BACKEND = process.env.REACT_APP_BACKEND_URL;
const portalApi = axios.create({ baseURL: PORTAL_BACKEND });

export default function AssetAiAssistant({ form, setForm }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [applied, setApplied] = useState(null); // { asset_type, category, ... }

  const extract = async () => {
    if (!text.trim()) { setMsg({ type: "err", text: "Paste some machine specs first." }); return; }
    setBusy(true); setMsg(null); setApplied(null);
    try {
      const { data } = await portalApi.post("/api/ai/asset-extract", { text: text.trim() });
      const patch = {};
      if (data.asset_type)     patch.asset_type = data.asset_type;
      if (data.category)       patch.category = data.category;
      if (data.machine_model)  patch.machine_model = data.machine_model;
      if (data.engine_config)  patch.engine_config = data.engine_config;
      if (data.serial_number)  patch.serial_number = data.serial_number;
      if (Object.keys(patch).length === 0) {
        setMsg({ type: "err", text: "AI couldn't find any recognisable fields — try more specs." });
      } else {
        setForm((f) => ({ ...f, ...patch }));
        setApplied(patch);
        setMsg({ type: "ok", text: `Pre-filled ${Object.keys(patch).length} field${Object.keys(patch).length === 1 ? "" : "s"}. Review and edit as needed.` });
      }
    } catch (ex) {
      setMsg({ type: "err", text: apiError(ex) });
    } finally { setBusy(false); }
  };

  return (
    <div
      className="rounded-xl p-3"
      style={{
        background: "linear-gradient(135deg, rgba(245,130,32,0.08), rgba(245,130,32,0.02))",
        border: "1px solid rgba(245,130,32,0.25)",
      }}
      data-testid="asset-ai-panel"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 text-left"
        data-testid="asset-ai-toggle"
      >
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: "rgba(245,130,32,0.20)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.35)" }}
        >
          <Sparkles className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--primary)" }}>AI Assistant</div>
          <div className="text-sm font-black leading-tight">Pre-fill from pasted machine specs</div>
          <div className="text-[11px] text-muted mt-0.5">
            Paste a datasheet or dealer email — AI extracts Asset Type, Model, Engine Config, Serial Number.
          </div>
        </div>
        <div className="text-[10px] uppercase tracking-widest font-black" style={{ color: "var(--primary)" }}>
          {open ? "Close" : "Open"}
        </div>
      </button>

      {open && (
        <div className="mt-3 space-y-2">
          <textarea
            className="textarea"
            rows={4}
            placeholder="e.g. Hitachi EX8000-6 excavator, twin Cummins QSK60 engines, serial number ABC-04512, dispatched 2023 to Whitehaven Coal…"
            value={text}
            onChange={(e) => setText(e.target.value)}
            data-testid="asset-ai-input"
            disabled={busy}
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={extract}
              disabled={busy || !text.trim()}
              data-testid="asset-ai-extract"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {busy ? "Reading specs…" : "Extract & pre-fill"}
            </button>
            {text && !busy && (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => { setText(""); setMsg(null); setApplied(null); }}
                data-testid="asset-ai-clear"
              >
                Clear
              </button>
            )}
            <div className="text-[10px] uppercase tracking-widest text-muted ml-auto">
              Powered by GPT
            </div>
          </div>
          {msg && (
            <div className={`pill ${msg.type === "ok" ? "pill-green" : "pill-red"} !text-xs !py-2 !px-3 justify-start`} data-testid="asset-ai-msg">
              {msg.type === "ok" ? <Check className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />} {msg.text}
            </div>
          )}
          {applied && (
            <div className="grid grid-cols-2 gap-1.5 text-[11px] font-mono-tight" data-testid="asset-ai-applied">
              {Object.entries(applied).map(([k, v]) => (
                <div key={k} className="px-2 py-1 rounded" style={{ background: "rgba(74,222,128,0.08)", border: "1px solid rgba(74,222,128,0.25)" }}>
                  <span className="text-muted">{k}:</span> <span className="text-white">{v}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
